/**
 * PinInputController — PIN 6자리 입력 인터랙션
 *
 * 기능:
 *  - 각 칸에 숫자 입력 시 다음 칸으로 자동 포커스 이동
 *  - 백스페이스 시 이전 칸으로 포커스 이동
 *  - 6자리 완성 시 hidden input에 전체 PIN 값 설정
 *  - PIN 불일치 시 진동 이펙트 (CSS animation)
 *  - 마스킹 처리 (입력 후 ● 표시)
 */
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "digit",          // PIN 입력 칸 6개
    "confirmDigit",   // PIN 확인 입력 칸 6개
    "pinValue",       // hidden input — PIN 값
    "confirmValue",   // hidden input — PIN 확인 값
    "pinGroup",       // PIN 입력 그룹 컨테이너
    "confirmGroup",   // PIN 확인 그룹 컨테이너
    "errorMessage",   // 에러 메시지 영역
    "submitBtn"       // 제출 버튼
  ]

  static DIGIT_PATTERN = /^\d$/
  static MASK_DELAY = 150

  connect() {
    // 첫 번째 칸에 자동 포커스
    if (this.digitTargets.length > 0) {
      this.digitTargets[0].focus()
    }
  }

  // ── PIN 입력 핸들러 ────────────────────────────────────
  onInput(event) {
    const input = event.target
    const index = parseInt(input.dataset.index)
    const value = input.value

    // 숫자가 아닌 입력 제거
    if (!this.constructor.DIGIT_PATTERN.test(value)) {
      input.value = ""
      return
    }

    input.classList.add("filled")

    setTimeout(() => {
      if (input.value) {
        input.type = "password"
        input.style.webkitTextSecurity = "disc"
      }
    }, this.constructor.MASK_DELAY)

    // 다음 칸으로 자동 포커스
    if (index < 5 && this.digitTargets[index + 1]) {
      this.digitTargets[index + 1].focus()
    }

    this.updatePinValue()
    this.validateAndToggleSubmit()
  }

  // ── PIN 키다운 핸들러 (백스페이스 처리) ─────────────────
  onKeydown(event) {
    const input = event.target
    const index = parseInt(input.dataset.index)

    if (event.key === "Backspace") {
      if (input.value === "" && index > 0) {
        // 현재 칸이 비어있으면 이전 칸으로 이동
        const prev = this.digitTargets[index - 1]
        prev.value = ""
        prev.type = "text"
        prev.style.webkitTextSecurity = "none"
        prev.classList.remove("filled")
        prev.focus()
        this.updatePinValue()
        this.validateAndToggleSubmit()
      } else {
        // 현재 칸 클리어
        input.value = ""
        input.type = "text"
        input.style.webkitTextSecurity = "none"
        input.classList.remove("filled")
        this.updatePinValue()
        this.validateAndToggleSubmit()
      }
    }

    if (
      !this.constructor.DIGIT_PATTERN.test(event.key) &&
      !["Backspace", "Tab", "ArrowLeft", "ArrowRight"].includes(event.key)
    ) {
      event.preventDefault()
    }
  }

  onConfirmInput(event) {
    const input = event.target
    const index = parseInt(input.dataset.index)
    const value = input.value

    if (!this.constructor.DIGIT_PATTERN.test(value)) {
      input.value = ""
      return
    }

    input.classList.add("filled")

    setTimeout(() => {
      if (input.value) {
        input.type = "password"
        input.style.webkitTextSecurity = "disc"
      }
    }, this.constructor.MASK_DELAY)

    if (index < 5 && this.confirmDigitTargets[index + 1]) {
      this.confirmDigitTargets[index + 1].focus()
    }

    this.updateConfirmValue()
    this.validateAndToggleSubmit()
  }

  // ── PIN 확인 키다운 핸들러 ──────────────────────────────
  onConfirmKeydown(event) {
    const input = event.target
    const index = parseInt(input.dataset.index)

    if (event.key === "Backspace") {
      if (input.value === "" && index > 0) {
        const prev = this.confirmDigitTargets[index - 1]
        prev.value = ""
        prev.type = "text"
        prev.style.webkitTextSecurity = "none"
        prev.classList.remove("filled")
        prev.focus()
        this.updateConfirmValue()
        this.validateAndToggleSubmit()
      } else {
        input.value = ""
        input.type = "text"
        input.style.webkitTextSecurity = "none"
        input.classList.remove("filled")
        this.updateConfirmValue()
        this.validateAndToggleSubmit()
      }
    }

    if (
      !this.constructor.DIGIT_PATTERN.test(event.key) &&
      !["Backspace", "Tab", "ArrowLeft", "ArrowRight"].includes(event.key)
    ) {
      event.preventDefault()
    }
  }
  onFocus(event) {
    event.target.select()
  }

  // ── hidden input에 전체 PIN 값 동기화 ───────────────────
  updatePinValue() {
    const pin = this.digitTargets.map(d => d.value).join("")
    this.pinValueTarget.value = pin
  }

  updateConfirmValue() {
    const confirm = this.confirmDigitTargets.map(d => d.value).join("")
    this.confirmValueTarget.value = confirm
  }

  // ── 유효성 검증 + 버튼 토글 ─────────────────────────────
  validateAndToggleSubmit() {
    const pin = this.pinValueTarget.value
    const confirm = this.confirmValueTarget.value
    const pinComplete = pin.length === 6
    const confirmComplete = confirm.length === 6

    // 에러 상태 초기화
    this.clearErrors()

    if (pinComplete && confirmComplete) {
      if (pin === confirm) {
        // 일치 — 버튼 활성화
        this.submitBtnTarget.disabled = false
        this.submitBtnTarget.style.opacity = "1"
      } else {
        // 불일치 — 진동 이펙트 + 에러 표시
        this.showMismatchError()
      }
    } else {
      // 미완성 — 버튼 비활성화
      this.submitBtnTarget.disabled = true
      this.submitBtnTarget.style.opacity = "0.4"
    }
  }

  // ── PIN 불일치 에러 표시 ─────────────────────────────────
  showMismatchError() {
    this.submitBtnTarget.disabled = true
    this.submitBtnTarget.style.opacity = "0.4"

    // 에러 메시지 표시
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = this.errorMessageTarget.dataset.mismatchText || "PIN mismatch"
      this.errorMessageTarget.classList.remove("hidden")
    }

    // 확인 칸에 에러 스타일 + 진동 이펙트
    this.confirmDigitTargets.forEach(d => d.classList.add("error"))
    this.confirmGroupTarget.classList.add("pin-shake")

    setTimeout(() => {
      this.confirmGroupTarget.classList.remove("pin-shake")
    }, 500)
  }

  // ── 에러 상태 초기화 ────────────────────────────────────
  clearErrors() {
    this.confirmDigitTargets.forEach(d => d.classList.remove("error"))
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add("hidden")
    }
  }
}
